import { forwardRef, useCallback, useState } from 'react'
import * as React from 'react'
import Overlay from 'shared/components/Overlay/Overlay'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { KEYBOARD } from 'shared/helpers/constants'
import { HandlerOf, HandlerOfOptional } from 'shared/helpers/typeHelper'

import { AutocompleteContainer, Option, Options } from './AutoComplete.styles'

export interface AutoCompleteOptionType {
  label: string
  value: string
}

export interface AutoCompleteProps {
  options: Array<AutoCompleteOptionType>
  onChange?: HandlerOfOptional<string>
  optionSelect: HandlerOf<AutoCompleteOptionType>
  value?: string
  name?: string
  error?: React.ReactElement | string | boolean
  label?: string
  placeholder?: string
  startAdornment?: React.ReactNode
  backgroundColor?: string
}

export const AutoComplete = forwardRef(
  (
    {
      options,
      onChange,
      optionSelect,
      value,
      name,
      error,
      label,
      placeholder,
      startAdornment,
      backgroundColor,
    }: AutoCompleteProps,
    ref
  ) => {
    const [openOptions, setOpenOptions] = useState(false)
    const [currentFocus, setCurrentFocus] = useState(-1)
    const hasOptions = options.length > 0
    const handleOverlayClick = useCallback(() => setOpenOptions(false), [])

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
      if (event.key === KEYBOARD.ESCAPE) setOpenOptions(false)
      if (openOptions) {
        if (event.key === KEYBOARD.ARROW_DOWN) {
          if (currentFocus + 1 >= options.length) {
            setCurrentFocus(0)
          } else {
            setCurrentFocus(currentFocus + 1)
          }
          event.preventDefault()
          event.stopPropagation()
        }

        if (event.key === KEYBOARD.ARROW_UP) {
          if (currentFocus <= 0) {
            setCurrentFocus(options.length - 1)
          } else {
            setCurrentFocus(currentFocus - 1)
          }
          event.preventDefault()
          event.stopPropagation()
        }

        if (event.key === KEYBOARD.ENTER) {
          event.preventDefault()
          event.stopPropagation()
          if (currentFocus !== -1) optionClick(options[currentFocus])
          setOpenOptions(false)
        }
      }
    }

    const optionClick = (option: AutoCompleteOptionType) => {
      onChange?.(option.value)
      optionSelect(option)
      setOpenOptions(false)
    }

    const onFocus = () => {
      setOpenOptions(true)
    }

    const handleChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event?.target?.value)
      setOpenOptions(true)
      setCurrentFocus(-1)
    }

    return (
      <AutocompleteContainer onKeyDown={handleKeyDown}>
        <TextInput
          ref={ref}
          name={name}
          onChange={handleChange}
          value={value}
          error={Boolean(error)}
          label={label}
          placeholder={placeholder}
          onFocus={onFocus}
          disableBorder={Boolean(options.length)}
          startAdornment={startAdornment}
          backgroundColor={backgroundColor}
        />
        {hasOptions && openOptions && (
          <>
            <Overlay onClick={handleOverlayClick} isTransparent={true} />
            <Options>
              {options.map((option: AutoCompleteOptionType, i: number) => (
                <Option
                  key={option.label}
                  onClick={() => optionClick(option)}
                  $isFocused={i === currentFocus}
                >
                  {option.label}
                </Option>
              ))}
            </Options>
          </>
        )}
      </AutocompleteContainer>
    )
  }
)
